import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input, linkedSignal, output } from '@angular/core';
import { ESplashLoaderComponent } from '@shared/components/loaders/e-splash-loader/e-splash-loader.component';
import {
  IChingCoinFace,
  IChingHexagram,
  IChingHexagramCommentary,
  IChingIndexEntry,
  IChingMethodContent,
  IChingMethodStep,
  IChingMethodTerm,
  IChingMethodTrigram,
  IChingReading,
  IChingTrigramId,
} from '../../core/iching.models';
import { HdIChingHexagramFigureComponent } from '../../figure/hd-iching-hexagram-figure.component';
import { HdIChingHexagramReaderComponent, IChingTrigramName } from '../../reader/hd-iching-hexagram-reader.component';
import { buildIChingTermMatcher, termsInText } from '../../reader/iching-terms.util';
import { injectIChingTranslate } from '../iching-consult-translate';
import { HdIChingStepHelpComponent } from '../steps/hd-iching-step-help.component';
import { HdIChingChangeComponent } from './hd-iching-change.component';
import { HdIChingMovingLinesComponent } from './hd-iching-moving-lines.component';
import { HdIChingPracticeComponent } from './hd-iching-practice.component';
import { HdIChingRootComponent } from './hd-iching-root.component';
import { HdIChingTrigramCardComponent } from './hd-iching-trigram-card.component';

/**
 * Fase 3 van de raadpleging, de duiding, in zes stappen: de situatie
 * (trigrammen, C1), de bewegende lijnen (C2), de wortel (C3), de tekst (C4),
 * het omslaghexagram (C5) en de praktijk (C6). Alle reflectiestate (relatie-
 * modus, naam zichtbaar, trefwoorden, lijstjes, notities) leeft in het concept
 * van de ouder; hier alleen of de uitleg van de vertaler al open mag.
 */
@Component({
  selector: 'hd-iching-reading',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ESplashLoaderComponent,
    HdIChingStepHelpComponent,
    HdIChingHexagramFigureComponent,
    HdIChingHexagramReaderComponent,
    HdIChingTrigramCardComponent,
    HdIChingMovingLinesComponent,
    HdIChingRootComponent,
    HdIChingChangeComponent,
    HdIChingPracticeComponent,
  ],
  templateUrl: './hd-iching-reading.component.html',
  styleUrl: './hd-iching-reading.component.scss',
})
export class HdIChingReadingComponent {
  readonly reading = input.required<IChingReading>();
  readonly method = input.required<IChingMethodContent>();
  readonly hexagram = input<IChingHexagram | undefined>(undefined);
  readonly changedHexagram = input<IChingHexagram | undefined>(undefined);
  readonly nuclearEntry = input<IChingIndexEntry | undefined>(undefined);
  readonly commentary = input<IChingHexagramCommentary | undefined>(undefined);
  readonly commentaryBusy = input(false, { transform: booleanAttribute });
  /** Muntzijden per worp (index 0 = lijn 1), voor de herkomst van de bewegende lijnen in C2. */
  readonly coinFaces = input<ReadonlyArray<readonly IChingCoinFace[]>>([]);
  readonly trigramNames = input<Readonly<Record<IChingTrigramId, IChingTrigramName>> | undefined>(undefined);
  readonly relationMode = input(false, { transform: booleanAttribute });
  readonly nameRevealed = input(false, { transform: booleanAttribute });
  readonly keywords = input<readonly string[]>([]);
  readonly notes = input('');
  readonly doList = input<readonly string[]>([]);
  readonly dontList = input<readonly string[]>([]);
  readonly saving = input(false, { transform: booleanAttribute });
  readonly saved = input(false, { transform: booleanAttribute });
  /** De raadpleging staat in het dagboek: "vandaag herlezen" kan. */
  readonly canRevisit = input(false, { transform: booleanAttribute });

  readonly relationModeChange = output<boolean>();
  readonly nameRevealedChange = output<boolean>();
  readonly keywordsChange = output<readonly string[]>();
  readonly notesChange = output<string>();
  readonly doListChange = output<readonly string[]>();
  readonly dontListChange = output<readonly string[]>();
  readonly commentaryRequested = output<void>();
  readonly save = output<void>();
  readonly copy = output<void>();
  readonly revisitRequested = output<void>();
  readonly openHexagram = output<number>();

  protected readonly t = injectIChingTranslate();

  /** Uitleg van de vertaler pas na je eigen lezing; dicht bij elk nieuw hexagram. */
  protected readonly showExplanations = linkedSignal({ source: () => this.reading().primary, computation: () => false });

  protected readonly lowerTrigram = computed(() => this.methodTrigram(this.reading().lower));
  protected readonly upperTrigram = computed(() => this.methodTrigram(this.reading().upper));
  protected readonly resonanceTrigram = computed(() => this.methodTrigram(this.reading().resonance));

  /** Bij zes bewegende lijnen in hexagram 1 of 2 geldt de extra tekst (Wilhelm: "Als alle lijnen..."). */
  protected readonly showAllLinesHint = computed(() => {
    const reading = this.reading();
    return (
      reading.movingPositions.length === 6 &&
      (reading.primary === 1 || reading.primary === 2) &&
      this.hexagram()?.text.allLines !== undefined
    );
  });

  private readonly termMatcher = computed(() => buildIChingTermMatcher(this.method().terms));

  /** Begrippen uit de methode die als heel woord in Oordeel, Beeld of de bewegende lijnen voorkomen. */
  protected readonly termsInText = computed<IChingMethodTerm[]>(() => {
    const hexagram = this.hexagram();
    if (hexagram === undefined) {
      return [];
    }
    const moving = this.reading().movingPositions;
    const text = [
      ...hexagram.text.judgement.text,
      ...hexagram.text.image.text,
      ...hexagram.text.lines.filter(line => moving.includes(line.position)).flatMap(line => line.text),
    ].join('\n');
    return termsInText(text, this.method().terms, this.termMatcher());
  });

  protected step(id: IChingMethodStep['id']): IChingMethodStep | undefined {
    return this.method().steps.steps.find(step => step.id === id);
  }

  protected hasMovingIn(trigram: 'lower' | 'upper'): boolean {
    return this.reading().movingPositions.some(position => (trigram === 'lower' ? position <= 3 : position >= 4));
  }

  protected toggleKeyword(word: string): void {
    const keywords = this.keywords();
    const exists = keywords.some(keyword => keyword.toLowerCase() === word);
    this.keywordsChange.emit(exists ? keywords.filter(keyword => keyword.toLowerCase() !== word) : [...keywords, word]);
  }

  private methodTrigram(id: IChingTrigramId): IChingMethodTrigram | undefined {
    return this.method().trigrams.find(trigram => trigram.id === id);
  }
}

